import React, { useCallback, useEffect, useState } from "react";
import { fetchJson } from "../../lib/api";
import type { NotificationsResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";

export const NotificationsView = ({ houseId }: { houseId: number | null }) => {
  const { t } = useI18n();
  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNotifications = useCallback(async () => {
    if (!houseId) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await fetchJson<{ status: string; lastSeenAt: string | null; unseenCount: number }>(
        `/api/houses/${houseId}/notifications/seen`,
        { method: "POST" },
      );
      const response = await fetchJson<NotificationsResponse>(`/api/houses/${houseId}/notifications`);
      setData(response);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : t("notifications.loadFailed", "Failed to load notifications"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [houseId, t]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("notifications.title", "Notifications")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("notifications.selectHouse", "Select a house to review notifications.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <div>
          <CardTitle>{t("notifications.title", "Notifications")}</CardTitle>
          <p className="text-sm text-muted">
            {t("notifications.subtitle", "Feed of updates and checks for this house.")}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadNotifications()} disabled={isLoading}>
          {t("notifications.refresh", "Refresh")}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <p className="text-sm text-muted">{t("notifications.loading", "Loading notifications...")}</p>
        ) : null}
        {error ? <p className="text-sm text-error">{error}</p> : null}
        {!isLoading && !error && data && data.notifications.length === 0 ? (
          <p className="text-sm text-muted">
            {t("notifications.empty", "No notifications yet for this house.")}
          </p>
        ) : null}
        {!isLoading && !error && data ? (
          <p className="text-xs text-muted">
            {t("notifications.unseenCount", "Unseen notifications: {count}", {
              count: data.unseenCount,
            })}
          </p>
        ) : null}
        <div className="space-y-2">
          {data?.notifications.map((notification) => (
            <div key={notification.id} className="rounded-lg border p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-ink">{notification.title}</p>
                <span className="text-xs text-muted">
                  {new Date(notification.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted">{notification.message}</p>
              {!notification.seen ? (
                <p className="mt-2 text-xs font-medium text-blue-700">
                  {t("notifications.unseen", "Unseen")}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
